import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Helmet } from 'react-helmet-async';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log detailed error information
    console.group('ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to error reporting services
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
        componentStack: errorInfo.componentStack
      });
    }
    
    // Send error to backend for logging
    if (process.env.NODE_ENV === 'production') {
      try {
        fetch('/api/log-error', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            error: error.toString(),
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            url: window.location.href,
            timestamp: new Date().toISOString()
          })
        });
      } catch (logError) {
        console.error('Failed to log error:', logError);
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <>
          <Helmet>
            <title>Ett fel uppstod - hittadeals.com</title>
            <meta name="description" content="Ett oväntat fel uppstod på hittadeals.com. Vi arbetar på att lösa problemet." />
            <meta name="robots" content="noindex" />
          </Helmet>
          
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              p: 2
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                maxWidth: 600,
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" color="error" gutterBottom>
                Oops! Något gick fel
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Ett oväntat fel uppstod. Vi ber om ursäkt för besväret.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={this.handleReload}
                >
                  Ladda om sidan
                </Button>
                
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={this.handleGoHome}
                >
                  Gå till startsidan
                </Button>
              </Box>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ mt: 3, textAlign: 'left' }}>
                  <Typography variant="h6" color="error">
                    Utvecklingsinfo:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="pre" 
                    sx={{ 
                      backgroundColor: '#f5f5f5', 
                      p: 2, 
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.75rem'
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo.componentStack}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;